import { sleep } from '@iotize/common/utility';
import { ConnectionState } from '@iotize/tap/protocol/api';
import {
  BleComError,
  BleConfig,
  CharacteristicAdapterInterface,
  CharacteristicProperties,
  DescriptorAdapterInterface,
  PeripheralAdapterInterface,
  ServiceAdapterInterface,
} from '@iotize/tap/protocol/ble/common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export class MockPeripheralAdapter
  implements PeripheralAdapterInterface<MockServiceAdapter>
{
  services: Partial<Record<string, MockServiceAdapter>> = {};

  stateChange = new BehaviorSubject<ConnectionState>(
    this.options.state === 'connected'
      ? ConnectionState.CONNECTED
      : ConnectionState.DISCONNECTED,
  );

  constructor(
    public options: {
      state: 'connected' | 'disconnected';
      connectDelay: number;
      disconnectDelay: number;
      name: string;
      id: string;
    } = {
      state: 'connected',
      connectDelay: 100,
      disconnectDelay: 100,
      name: 'MockPeripheral',
      id: 'MockPeripheral',
    },
  ) {
    this.services[BleConfig.services.lwm2m.service] = new MockServiceAdapter(
      BleConfig.services.lwm2m.service,
      [
        new MockCharacteristicAdapter(BleConfig.services.lwm2m.charac, {
          write: true,
          read: true,
          authenticatedSignedWrites: false,
          broadcast: false,
          indicate: true,
          notify: true,
          reliableWrite: false,
          writableAuxiliaries: false,
          writeWithoutResponse: true,
        }),
      ],
    );
    this.services[BleConfig.services.fastLwm2m.service] =
      new MockServiceAdapter(BleConfig.services.fastLwm2m.service, [
        new MockCharacteristicAdapter(BleConfig.services.fastLwm2m.charac, {
          write: true,
          read: true,
          authenticatedSignedWrites: false,
          broadcast: false,
          indicate: true,
          notify: true,
          reliableWrite: false,
          writableAuxiliaries: false,
          writeWithoutResponse: true,
        }),
      ]);

    this.stateChange.subscribe((newState) => {
      if (newState === ConnectionState.CONNECTED) {
        this.options.state === 'connected';
      } else {
        this.options.state = 'disconnected';
      }
    });
  }

  get id() {
    return this.options.id;
  }

  get name() {
    return this.options.name;
  }

  get state() {
    return this.options.state;
  }

  async connect() {
    await sleep(this.options.connectDelay);
    this.stateChange.next(ConnectionState.CONNECTED);
  }

  async disconnect() {
    await sleep(this.options.disconnectDelay);
    this.stateChange.next(ConnectionState.DISCONNECTED);
  }

  async discoverServices() {
    return this.services;
  }

  async getService(uuid: string): Promise<MockServiceAdapter> {
    if (!this.services[uuid]) {
      throw BleComError.serviceNotFound(uuid);
    }
    return this.services[uuid]!;
  }

  close() {
    this.stateChange.complete();
  }
}

export class MockServiceAdapter implements ServiceAdapterInterface {
  constructor(
    public uuid: string,
    public characteristics: CharacteristicAdapterInterface<DescriptorAdapterInterface>[] = [],
  ) {}

  async getCharacteristics(): Promise<
    CharacteristicAdapterInterface<DescriptorAdapterInterface>[]
  > {
    return this.characteristics;
  }

  async getCharacteristic(
    charcUUID: string,
  ): Promise<CharacteristicAdapterInterface<DescriptorAdapterInterface>> {
    const charac = this.characteristics.find((c) => c.uuid === charcUUID);
    if (!charac) {
      throw BleComError.charcacteristicNotFound(charcUUID);
    }
    return charac;
  }
}

export class MockCharacteristicAdapter
  implements CharacteristicAdapterInterface<DescriptorAdapterInterface>
{
  public notificationEnabled = false;

  data: Observable<{ data: Uint8Array; isNotification: boolean }> =
    new Subject();

  constructor(
    public readonly uuid: string,
    public readonly properties: CharacteristicProperties,
    public readonly descriptors: DescriptorAdapterInterface[] = [],
  ) {}

  async write(
    data: Uint8Array,
    writeWithoutResponse: boolean,
  ): Promise<Uint8Array> {
    throw new Error('Method not implemented.');
  }
  async read(): Promise<Uint8Array> {
    throw new Error('Method not implemented.');
  }
  async enableNotifications(enabled: boolean): Promise<void> {
    this.notificationEnabled = enabled;
  }
  async getDescriptors(): Promise<DescriptorAdapterInterface[]> {
    return this.descriptors;
  }
}
